import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { IonSelect, IonSelectOption, IonToggle, useIonToast } from "@ionic/react";
import { FormattedMessage, useIntl } from "react-intl";

// Components
import SkeletonText from "../../../SkeletonText";

// Api
import AiApi from "../../../../api/ai";
import { useEffect } from "react";

type FollowUpLikelihood = "high" | "default" | "low";

type FollowUpsSettings = {
  active: boolean;
  delayMinutes: number;
  likelihood: FollowUpLikelihood;
};

const FOLLOW_UP_DELAY_OPTIONS = [30].concat(
  Array.from({ length: 23 }, (_, index) => (index + 1) * 60),
);
const SELECT_RIGHT_STYLE = {
  width: "fit-content",
  minWidth: "130px",
  maxWidth: "220px",
  marginLeft: "auto",
  flex: "0 0 auto",
  textAlign: "right",
} as const;

export default function FollowUps() {
  const intl = useIntl();

  const queryClient = useQueryClient();

  const [followUps, setFollowUps] = useState<FollowUpsSettings>({
    active: false,
    delayMinutes: 30,
    likelihood: "default",
  });

  const [present] = useIonToast();
  const { data, isLoading: isRequesting } = useQuery({
    queryFn: () => AiApi.getAiSettings(),
    queryKey: ["getAiSettings"],
    refetchOnWindowFocus: false,
  });

  const handleToggleFollowUps = async (value: boolean) => {
    const { err } = await AiApi.putAiSettingsByField("followUps.active", value);
    if (err) {
      present({
        message: intl.formatMessage({ id: "common.tryItLater" }),
        duration: 3000,
        color: "light",
        position: "top",
        cssClass: "error-toast",
      });
    }
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["getAiSettings"] }),
      queryClient.invalidateQueries({ queryKey: ["getAiStatus"] }),
    ]);
  };

  const handleDelayChange = async (value: number) => {
    const { err } = await AiApi.putAiSettingsByField(
      "followUps.delayMinutes",
      value,
    );
    if (err) {
      present({
        message: intl.formatMessage({ id: "common.tryItLater" }),
        duration: 3000,
        color: "light",
        position: "top",
        cssClass: "error-toast",
      });
      return;
    }
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["getAiSettings"] }),
      queryClient.invalidateQueries({ queryKey: ["getAiStatus"] }),
    ]);
  };

  const handleLikelihoodChange = async (value: FollowUpLikelihood) => {
    const { err } = await AiApi.putAiSettingsByField("followUps.likelihood", value);
    if (err) {
      present({
        message: intl.formatMessage({ id: "common.tryItLater" }),
        duration: 3000,
        color: "light",
        position: "top",
        cssClass: "error-toast",
      });
      return;
    }
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["getAiSettings"] }),
      queryClient.invalidateQueries({ queryKey: ["getAiStatus"] }),
    ]);
  };

  useEffect(() => {
    if (data?.data?.aiSettings?.followUps) {
      setFollowUps({
        active: Boolean(data.data.aiSettings.followUps.active),
        delayMinutes: Number(data.data.aiSettings.followUps.delayMinutes || 30),
        likelihood: (data.data.aiSettings.followUps.likelihood ||
          "default") as FollowUpLikelihood,
      });
    }
  }, [data]);

  const isFunctionActive = followUps?.active || false;

  return isRequesting ? (
    <SkeletonText rows={12} />
  ) : (
    <div className="flex flex-column gap-4 pb-5">
      {/* Enable escalations (globally) */}
      <div className="visito-card">
        <div className="visito-card-body">
          <div className="flex align-items-start justify-content-between gap-3">
            <div>
              <label className="mb-0 d-block" htmlFor="enable-help">
                <FormattedMessage
                  id={
                    isFunctionActive
                      ? "ai.settings.followUps.enable"
                      : "ai.settings.followUps.disabled"
                  }
                />
              </label>
              <p className="description mb-0 fs-2 mt-1">
                <FormattedMessage id="ai.settings.followUps.enable.desc" />
              </p>
            </div>
            <IonToggle
              id="enable-help"
              name="enable-help"
              checked={isFunctionActive}
              onIonChange={(ev) => handleToggleFollowUps(ev.detail.checked)}
              mode="ios"
              className="ios visito-toggle"
            />
          </div>
        </div>
      </div>

      <div className="visito-card">
        <div className="visito-card-body">
          <div className="flex align-items-start justify-content-between gap-3">
            <div>
              <label className="mb-0 d-block" htmlFor="follow-up-delay">
                <FormattedMessage id="ai.settings.followUps.delay" />
              </label>
              <p className="description mb-0 fs-2 mt-1">
                <FormattedMessage id="ai.settings.followUps.delay.desc" />
              </p>
            </div>
            <IonSelect
              id="follow-up-delay"
              interface="popover"
              value={followUps.delayMinutes}
              style={SELECT_RIGHT_STYLE}
              onIonChange={(ev) => handleDelayChange(Number(ev.detail.value))}
            >
              {FOLLOW_UP_DELAY_OPTIONS.map((minutes) => (
                <IonSelectOption key={minutes} value={minutes}>
                  {minutes === 30
                    ? intl.formatMessage({
                        id: "ai.settings.followUps.delay.option.half",
                      })
                    : intl.formatMessage(
                        {
                          id: "ai.settings.followUps.delay.option.hours",
                        },
                        { hours: minutes / 60 },
                      )}
                </IonSelectOption>
              ))}
            </IonSelect>
          </div>
        </div>
      </div>

      <div className="visito-card">
        <div className="visito-card-body">
          <div className="flex align-items-start justify-content-between gap-3">
            <div>
              <label className="mb-0 d-block" htmlFor="follow-up-likelihood">
                <FormattedMessage id="ai.settings.followUps.likelihood" />
              </label>
              <p className="description mb-0 fs-2 mt-1">
                <FormattedMessage id="ai.settings.followUps.likelihood.desc" />
              </p>
            </div>
            <IonSelect
              id="follow-up-likelihood"
              interface="popover"
              value={followUps.likelihood}
              style={SELECT_RIGHT_STYLE}
              onIonChange={(ev) =>
                handleLikelihoodChange(ev.detail.value as FollowUpLikelihood)
              }
            >
              <IonSelectOption value="high">
                {intl.formatMessage({ id: "ai.settings.followUps.likelihood.high" })}
              </IonSelectOption>
              <IonSelectOption value="default">
                {intl.formatMessage({
                  id: "ai.settings.followUps.likelihood.default",
                })}
              </IonSelectOption>
              <IonSelectOption value="low">
                {intl.formatMessage({ id: "ai.settings.followUps.likelihood.low" })}
              </IonSelectOption>
            </IonSelect>
          </div>
        </div>
      </div>
    </div>
  );
}
